import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import { inventoryAPI, modifierGroupAPI } from '../../services';
import { toast } from 'react-toastify';

const emptyIngredient = () => ({ material_id: '', quantity: '', unit_note: '' });

export default function RecipeFormModal({ show, onHide, menuItem, recipe, materials, onSuccess }) {
  const [ingredients, setIngredients] = useState([emptyIngredient()]);
  const [adjustments, setAdjustments] = useState([]);
  const [initialRecipeIds, setInitialRecipeIds] = useState([]);
  const [initialAdjIds, setInitialAdjIds] = useState([]);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadedForRef = useRef(null);

  const activeMaterials = (materials || []).filter((m) => m.is_active !== false);

  // Effect A: init form từ recipe prop
  useEffect(() => {
    if (!show) return;
    const recs = recipe?.recipes || [];
    const mapped = recs.length
      ? recs.map((i) => ({ id: i.id, material_id: i.material_id || '', quantity: i.quantity?.toString() || '', unit_note: i.unit_note || '' }))
      : [emptyIngredient()];
    setIngredients(mapped);
    setInitialRecipeIds(mapped.map((i) => i.id).filter(Boolean));
    const adjs = (recipe?.adjustments || []).map((a) => ({
      id: a.id,
      modifier_option_id: a.modifier_option_id,
      material_id: a.material_id,
      adjustment_type: a.adjustment_type,
      quantity: String(a.quantity),
    }));
    setAdjustments(adjs);
    setInitialAdjIds(adjs.map((a) => a.id).filter(Boolean));
  }, [show, recipe]);

  // Effect B: load modifier groups, chỉ khi menuItem thực sự thay đổi
  useEffect(() => {
    if (!show) {
      loadedForRef.current = null;
      return;
    }
    if (!menuItem?.id) return;
    const enabledIds = menuItem.enabled_modifier_group_ids || [];
    const key = `${menuItem.id}:${JSON.stringify(enabledIds)}`;
    if (loadedForRef.current === key) return;
    loadedForRef.current = key;
    if (!enabledIds.length) { setAvailableOptions([]); return; }
    modifierGroupAPI.getModifierGroups().then((groups) => {
      const list = groups?.data || groups || [];
      const filtered = list.filter((g) => enabledIds.includes(g.id) && g.is_active !== false);
      setAvailableOptions(filtered.flatMap((g) =>
        (g.options || []).map((o) => ({ groupName: g.name, optionId: o.id, optionName: o.name }))
      ));
    }).catch(() => {
      loadedForRef.current = null;
      setAvailableOptions([]);
    });
  }, [show, menuItem]);

  const updateIngredient = (idx, field, value) =>
    setIngredients((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const addIngredient = () => setIngredients((prev) => [...prev, emptyIngredient()]);
  const removeIngredient = (idx) => setIngredients((prev) => prev.filter((_, i) => i !== idx));

  const updateAdj = (idx, field, value) =>
    setAdjustments((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const removeAdj = (idx) => setAdjustments((prev) => prev.filter((_, i) => i !== idx));

  const addAdj = (optionId) =>
    setAdjustments((prev) => [...prev, { modifier_option_id: optionId, material_id: '', adjustment_type: 'REPLACE', quantity: '' }]);

  // Copy NVL gốc vào option làm REPLACE template, bỏ inactive + bỏ NVL đã có
  const copyFromBase = (optionId) => setAdjustments((prev) => {
    const existing = new Set(prev.filter((a) => a.modifier_option_id === optionId).map((a) => a.material_id));
    const rows = ingredients
      .filter((ing) =>
        ing.material_id &&
        Number(ing.quantity) > 0 &&
        activeMaterials.find((m) => m.id === ing.material_id) &&
        !existing.has(ing.material_id)
      )
      .map((ing) => ({ modifier_option_id: optionId, material_id: ing.material_id, adjustment_type: 'REPLACE', quantity: ing.quantity }));
    return [...prev, ...rows];
  });

  // Tính công thức thực tế khi khách chọn optionId (mirror deduction logic)
  const computeEffective = (optionId) => {
    const map = new Map();
    ingredients.forEach((ing) => {
      if (ing.material_id && Number(ing.quantity) > 0)
        map.set(ing.material_id, { qty: Number(ing.quantity), src: 'base' });
    });
    adjustments.filter((a) => a.modifier_option_id === optionId && a.material_id).forEach((a) => {
      const q = Number(a.quantity) || 0;
      if (a.adjustment_type === 'REPLACE') {
        map.set(a.material_id, { qty: q, src: 'adj' });
      } else {
        const cur = map.get(a.material_id)?.qty ?? 0;
        map.set(a.material_id, { qty: cur + q, src: 'adj' });
      }
    });
    return [...map.entries()].map(([mid, v]) => ({ materialId: mid, ...v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validIngredients = ingredients.filter((r) => r.material_id && Number(r.quantity) > 0);
    if (validIngredients.length === 0) { toast.error('Vui lòng nhập ít nhất một nguyên liệu'); return; }
    try {
      setLoading(true);
      await inventoryAPI.upsertRecipe({
        menu_item_id: menuItem.id,
        items: validIngredients.map((r) => ({
          material_id: r.material_id,
          quantity: Number(r.quantity),
          unit_note: r.unit_note || undefined,
        })),
      });
      // Delete base recipe rows that were removed
      const keptRecipeIds = new Set(ingredients.map((r) => r.id).filter(Boolean));
      for (const id of initialRecipeIds.filter((id) => !keptRecipeIds.has(id))) {
        await inventoryAPI.deleteRecipe(id);
      }
      const validAdjs = adjustments.filter(
        (a) => a.modifier_option_id && a.material_id && Number(a.quantity) >= 0
      );
      for (const a of validAdjs) {
        await inventoryAPI.upsertAdjustment({
          menu_item_id: menuItem.id,
          modifier_option_id: a.modifier_option_id,
          material_id: a.material_id,
          adjustment_type: a.adjustment_type,
          quantity: Number(a.quantity),
        });
      }
      const currentIds = new Set(adjustments.map((a) => a.id).filter(Boolean));
      for (const id of initialAdjIds.filter((id) => !currentIds.has(id))) {
        await inventoryAPI.deleteAdjustment(id);
      }
      toast.success('Đã lưu công thức');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Không thể lưu công thức');
    } finally {
      setLoading(false);
    }
  };

  const groupedOptions = availableOptions.reduce((acc, o) => {
    if (!acc[o.groupName]) acc[o.groupName] = [];
    acc[o.groupName].push(o);
    return acc;
  }, {});

  // Render material cell: badge nếu NVL đã inactive
  const renderMaterialCell = (materialId, onChange) => {
    const isInactive = materialId && !activeMaterials.find((m) => m.id === materialId);
    if (isInactive) {
      const mat = (materials || []).find((m) => m.id === materialId);
      return (
        <div className="d-flex align-items-center gap-1 py-1">
          <span className="small">{mat?.name || materialId}</span>
          <Badge bg="warning" text="dark" className="small">ngừng sử dụng</Badge>
        </div>
      );
    }
    return (
      <Form.Select value={materialId} onChange={onChange} size="sm">
        <option value="">-- Chọn --</option>
        {activeMaterials.map((m) => (
          <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
        ))}
      </Form.Select>
    );
  };

  const getMaterialName = (materialId) => {
    const mat = (materials || []).find((m) => m.id === materialId);
    return mat ? `${mat.name} (${mat.unit})` : materialId;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Công thức — {menuItem?.name || ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>

          {/* ── Công thức gốc ── */}
          <p className="fw-semibold mb-2">Nguyên liệu cơ bản</p>
          <Table bordered size="sm" responsive>
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Nguyên liệu <span className="text-danger">*</span></th>
                <th style={{ minWidth: 100 }}>Số lượng <span className="text-danger">*</span></th>
                <th style={{ minWidth: 140 }}>Ghi chú đơn vị</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {ingredients.map((row, idx) => {
                const mat = activeMaterials.find((m) => m.id === row.material_id);
                return (
                  <tr key={idx}>
                    <td>
                      {renderMaterialCell(row.material_id, (e) => updateIngredient(idx, 'material_id', e.target.value))}
                    </td>
                    <td>
                      <Form.Control
                        type="number" min="0.001" step="0.001" size="sm"
                        value={row.quantity}
                        onChange={(e) => updateIngredient(idx, 'quantity', e.target.value)}
                        placeholder={mat?.unit || ''}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text" size="sm" value={row.unit_note}
                        onChange={(e) => updateIngredient(idx, 'unit_note', e.target.value)}
                        placeholder="VD: 1 muỗng canh"
                      />
                    </td>
                    <td>
                      <Button variant="link" size="sm" className="text-danger p-0"
                        onClick={() => removeIngredient(idx)} disabled={ingredients.length === 1}>✕</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Button variant="outline-secondary" size="sm" onClick={addIngredient}>
            + Thêm nguyên liệu
          </Button>

          {/* ── Điều chỉnh theo option ── */}
          {availableOptions.length > 0 ? (
            <div className="mt-4">
              <p className="fw-semibold mb-1">Điều chỉnh theo lựa chọn</p>
              <p className="text-muted small mb-3">
                Khi khách chọn nhiều option, công thức cuối = gốc + tất cả điều chỉnh của các option đã chọn.
                Option không có điều chỉnh → dùng công thức gốc (mặc định).
              </p>

              {Object.entries(groupedOptions).map(([groupName, opts]) => (
                <div key={groupName} className="mb-4">
                  <p className="text-secondary fw-medium small text-uppercase mb-2">{groupName}</p>

                  {opts.map((option) => {
                    const optAdjs = adjustments.filter((a) => a.modifier_option_id === option.optionId);
                    const effective = computeEffective(option.optionId);
                    const hasAdjs = optAdjs.length > 0;

                    return (
                      <div key={option.optionId} className="mb-3 ps-3 border-start border-2">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="fw-medium">{option.optionName}</span>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-secondary" size="sm"
                              onClick={() => copyFromBase(option.optionId)}
                              disabled={ingredients.every((ing) => !ing.material_id || Number(ing.quantity) <= 0)}
                              title="Sao chép tất cả NVL gốc vào option này làm điểm bắt đầu"
                            >
                              Sao chép từ gốc
                            </Button>
                            <Button variant="outline-primary" size="sm" onClick={() => addAdj(option.optionId)}>
                              + NVL
                            </Button>
                          </div>
                        </div>

                        {!hasAdjs ? (
                          <p className="text-muted small fst-italic mb-0">
                            (chưa cấu hình — dùng công thức gốc)
                          </p>
                        ) : (
                          <>
                            {/* Bảng điều chỉnh (editable) */}
                            <p className="small text-secondary mb-1">Điều chỉnh:</p>
                            <Table bordered size="sm" className="mb-2">
                              <thead>
                                <tr>
                                  <th style={{ minWidth: 180 }}>NVL</th>
                                  <th style={{ minWidth: 120 }}>Kiểu</th>
                                  <th style={{ minWidth: 90 }}>Số lượng</th>
                                  <th style={{ width: 40 }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {optAdjs.map((row) => {
                                  const adjIdx = adjustments.indexOf(row);
                                  return (
                                    <tr key={adjIdx}>
                                      <td>
                                        {renderMaterialCell(row.material_id, (e) => updateAdj(adjIdx, 'material_id', e.target.value))}
                                      </td>
                                      <td>
                                        <Form.Select
                                          value={row.adjustment_type}
                                          onChange={(e) => updateAdj(adjIdx, 'adjustment_type', e.target.value)}
                                          size="sm"
                                        >
                                          <option value="REPLACE">Thay thế</option>
                                          <option value="ADD">Cộng thêm</option>
                                        </Form.Select>
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="number" min="0" step="0.001" size="sm"
                                          value={row.quantity}
                                          onChange={(e) => updateAdj(adjIdx, 'quantity', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Button variant="link" size="sm" className="text-danger p-0"
                                          onClick={() => removeAdj(adjIdx)}>✕</Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>

                            {/* Preview công thức thực tế (read-only) */}
                            <div className="rounded p-2 mb-1" style={{ background: '#f8f9fa' }}>
                              <p className="small text-secondary mb-1 fw-medium">
                                Công thức thực tế khi chọn "{option.optionName}":
                              </p>
                              <Table borderless size="sm" className="mb-0">
                                <tbody>
                                  {effective.map((e) => {
                                    const mat = (materials || []).find((m) => m.id === e.materialId);
                                    const isInactive = mat && mat.is_active === false;
                                    return (
                                      <tr key={e.materialId}>
                                        <td className="py-0 ps-0">
                                          <span className="small">{mat?.name || e.materialId}</span>
                                          {isInactive && (
                                            <Badge bg="warning" text="dark" className="ms-1 small">ngừng sử dụng</Badge>
                                          )}
                                        </td>
                                        <td className="py-0 text-end small">
                                          {e.qty} {mat?.unit || ''}
                                        </td>
                                        <td className="py-0 ps-2">
                                          {e.src === 'adj' ? (
                                            <Badge bg="primary" className="small">điều chỉnh</Badge>
                                          ) : (
                                            <Badge bg="secondary" className="small">mặc định←base</Badge>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : menuItem?.enabled_modifier_group_ids?.length > 0 ? (
            <p className="text-muted small mt-3">Đang tải tuỳ chọn modifier...</p>
          ) : (
            <p className="text-muted small mt-3">
              Món chưa có modifier — thêm modifier ở trang Quản lý món để cấu hình điều chỉnh NVL theo size/option.
            </p>
          )}

        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>Hủy</Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Đang lưu...' : 'Lưu công thức'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
